import { CdkDrag }      from '@angular/cdk/drag-drop';
import { Subscription } from 'rxjs';

import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output
} from '@angular/core';

import { BookingDiaryChipStretchModel } from './typings/booking-diary.drag';
import { BookingDiaryEventModel }       from './typings/booking-diary.event';
import { JobStatusBookingPipe }         from '../transaction-center/jobs/transaction-center.jobs.status-booking.pipe';

import {
  CLICK_SUPPRESS_MS,
  formatClockLabel,
  SLOT_MINUTES,
  snapMinutes
} from './booking-diary.layout';

import {
  isBookedStatus,
  statusCodeFor
} from '../transaction-center/jobs/transaction-center.jobs.status-tone';

const SLOT_HEIGHT : string = '--booking-diary-slot-height';

// One booking on the grid: who, when and what, with the whole detail in the tooltip
@Component({
  selector   : 'wor-booking-diary-event-chip',
  template: require('./booking-diary.event-chip.component.html')
})
export class BookingDiaryEventChipComponent implements OnChanges, OnInit, OnDestroy {
  @Input() event       : BookingDiaryEventModel;
  @Input() isBusy      : boolean = false;

  @Output() cancelled : EventEmitter<void>                   = new EventEmitter<void>();
  @Output() opened    : EventEmitter<BookingDiaryEventModel> = new EventEmitter<BookingDiaryEventModel>();
  @Output() resized   : EventEmitter<BookingDiaryChipStretchModel> = new EventEmitter<BookingDiaryChipStretchModel>();
  @Output() split     : EventEmitter<void>                   = new EventEmitter<void>();

  // held, not derived: the status wording costs a translation lookup and the grid redraws often
  statusLabel : string = '';
  tooltip     : string = '';

  private hasJustDragged    : boolean = false;
  private isResizing        : boolean = false;
  private resetTimer        : ReturnType<typeof setTimeout>;
  private resizeEdge        : 'end' | 'start' = 'end';
  private resizeStartHeight : number  = 0;
  private resizeStartTop    : number  = 0;
  private resizeStartY      : number  = 0;
  private slotHeight        : number  = 0;
  private subscription      : Subscription;

  private readonly onResizeMove = ( moveEvent : MouseEvent | TouchEvent ) : void => this.trackResize(moveEvent);
  private readonly onResizeUp   = () : void => this.endResize();

  constructor (
    @Optional() private cdkDrag  : CdkDrag,
    private elementRef           : ElementRef<HTMLElement>,
    private jobStatusBookingPipe : JobStatusBookingPipe
  ) {}

  ngOnChanges () : void {
    this.statusLabel = this.jobStatusBookingPipe.transform(statusCodeFor(this.event.jobStatus)) || '';

    this.tooltip = [
      this.event.customerDisplayName,
      this.event.displayName,
      this.timeLabel,
      this.statusLabel,
      this.event.vehicleDescription,
      this.event.description,
      this.splitLabel
    ]
      .filter(part => !!part)
      .join('\n');
  }

  // the click that follows a completed drag would otherwise count as opening the booking
  ngOnInit () : void {
    if ( this.cdkDrag ) {
      this.subscription = this.cdkDrag.ended.subscribe(() => this.suppressNextClick());
    }
  }

  ngOnDestroy () : void {
    clearTimeout(this.resetTimer);

    this.detachResizeListeners();

    if ( this.subscription ) {
      this.subscription.unsubscribe();
    }
  }

  // only one of several appointments can be cancelled; the last one leaves the diary from its card
  get canCancel () : boolean {
    return this.event.kind === 'booking' && this.event.totalSplits > 1;
  }

  // a split adds a part to the labour line; once a job card exists the booking is closed and the part would never show
  get canSplit () : boolean {
    return this.event.kind === 'booking'
      && !!this.event.eventItemId
      && isBookedStatus(this.event.jobStatus);
  }

  onCancel ( clickEvent : MouseEvent ) : void {
    clickEvent.preventDefault();
    clickEvent.stopPropagation();

    this.cancelled.emit();
  }

  // stopping propagation keeps the pointer-down from also starting a drag on the chip; the start
  // edge moves the top of the chip and holds its end, the end edge the other way about
  onResizeStart ( startEvent : MouseEvent | TouchEvent, edge : 'end' | 'start' ) : void {
    startEvent.preventDefault();
    startEvent.stopPropagation();

    // a booking still being read back has nothing to save a stretch against
    if ( !this.event.eventId ) {
      return;
    }

    this.isResizing        = true;
    this.resizeEdge        = edge;
    this.resizeStartHeight = this.elementRef.nativeElement.offsetHeight;
    this.resizeStartTop    = this.elementRef.nativeElement.offsetTop;
    this.resizeStartY      = this.pointY(startEvent);

    document.addEventListener('mousemove', this.onResizeMove);
    document.addEventListener('mouseup', this.onResizeUp);
    document.addEventListener('touchmove', this.onResizeMove);
    document.addEventListener('touchend', this.onResizeUp);
  }

  onSplit ( clickEvent : MouseEvent ) : void {
    clickEvent.preventDefault();
    clickEvent.stopPropagation();

    this.split.emit();
  }

  open () : void {
    if ( this.hasJustDragged || this.isResizing || !this.event.eventId ) {
      return;
    }

    this.opened.emit(this.event);
  }

  get splitLabel () : string {
    return this.event.totalSplits > 1
      ? `${ this.event.ordering || 1 }/${ this.event.totalSplits }`
      : '';
  }

  get timeLabel () : string {
    return `${ formatClockLabel(this.event.startMinutes) } – ${ formatClockLabel(this.event.endMinutes) }`;
  }

  private detachResizeListeners () : void {
    document.removeEventListener('mousemove', this.onResizeMove);
    document.removeEventListener('mouseup', this.onResizeUp);
    document.removeEventListener('touchmove', this.onResizeMove);
    document.removeEventListener('touchend', this.onResizeUp);
  }

  private endResize () : void {
    this.detachResizeListeners();

    const height       = this.elementRef.nativeElement.offsetHeight;
    const deltaMinutes = ( height - this.resizeStartHeight ) / this.pixelsPerMinute();
    const stretch      = this.resizeEdge === 'start'
      ? {
        endMinutes  : this.event.endMinutes,
        startMinutes: snapMinutes(this.event.startMinutes - deltaMinutes)
      }
      : {
        endMinutes  : snapMinutes(this.event.endMinutes + deltaMinutes),
        startMinutes: this.event.startMinutes
      };

    this.isResizing = false;

    this.suppressNextClick();

    if ( stretch.startMinutes === this.event.startMinutes && stretch.endMinutes === this.event.endMinutes ) {
      this.elementRef.nativeElement.style.height = `${ this.resizeStartHeight }px`;
      this.elementRef.nativeElement.style.top    = `${ this.resizeStartTop }px`;

      return;
    }

    this.resized.emit(stretch);
  }

  // the slot height is a published token, so a stretch reads the same scale the grid draws
  private pixelsPerMinute () : number {
    if ( !this.slotHeight ) {
      const value = getComputedStyle(this.elementRef.nativeElement).getPropertyValue(SLOT_HEIGHT);

      this.slotHeight = parseFloat(value) || 0;
    }

    return this.slotHeight / SLOT_MINUTES;
  }

  private pointY ( pointerEvent : MouseEvent | TouchEvent ) : number {
    if ( pointerEvent instanceof MouseEvent ) {
      return pointerEvent.clientY;
    }

    return pointerEvent.touches[0]
      ? pointerEvent.touches[0].clientY
      : this.resizeStartY;
  }

  private suppressNextClick () : void {
    this.hasJustDragged = true;

    clearTimeout(this.resetTimer);

    this.resetTimer = setTimeout(() => this.hasJustDragged = false, CLICK_SUPPRESS_MS);
  }

  private trackResize ( moveEvent : MouseEvent | TouchEvent ) : void {
    const delta    = this.pointY(moveEvent) - this.resizeStartY;
    const smallest = this.pixelsPerMinute() * SLOT_MINUTES;
    const height   = this.resizeEdge === 'start'
      ? Math.max(this.resizeStartHeight - delta, smallest)
      : Math.max(this.resizeStartHeight + delta, smallest);

    if ( this.resizeEdge === 'start' ) {
      this.elementRef.nativeElement.style.top = `${ this.resizeStartTop + this.resizeStartHeight - height }px`;
    }

    this.elementRef.nativeElement.style.height = `${ height }px`;
  }
}
